import { InputWithIcon } from '@/components/ui/vehicle/input-with-icon';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleFromIndexerStore } from '@/models/stores/vehicle-listings';
import type { VehicleFromIndexer } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Heater } from 'lucide-react';
import { useEffect, useState } from 'react';

export type TopFeaturesInputProps = {
  id: string;
  value: VehicleFromIndexer['top_features'];
  className?: string;
};

export function TopFeaturesInput({
  id,
  value,
  className,
}: TopFeaturesInputProps) {
  // Local state for the three input fields
  const [localFeatures, setLocalFeatures] = useState<string[]>([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);

  // Update local state when the prop value changes
  useEffect(() => {
    const features = value.split(';');
    setLocalFeatures([
      features[0] || '',
      features[1] || '',
      features[2] || '',
      features[3] || '',
      features[4] || '',
      features[5] || '',
    ]);
  }, [value]);

  const updateTopFeaturesMutation = useMutation({
    onMutate: async ({ topFeatures }) => {
      vehicleFromIndexerStore.updateTopFeatures({ id, topFeatures });
    },
    mutationFn: async ({
      topFeatures,
    }: {
      topFeatures: VehicleFromIndexer['top_features'];
    }) => {
      return await pdkAxios.post(`/inventory/vehicles/${id}/top_features`, {
        top_features: topFeatures,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating top features',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleLocalChange = (newValue: string, position: number) => {
    const newFeatures = [...localFeatures];
    newFeatures[position] = newValue;
    setLocalFeatures(newFeatures);
  };

  const handleBlur = () => {
    const topFeaturesString = localFeatures.join(';');
    // Only update if the value has actually changed
    if (topFeaturesString !== value) {
      updateTopFeaturesMutation.mutate({
        topFeatures: topFeaturesString,
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm text-gray-500">Top features</span>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-2">
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[0]}
            placeholder="Top features 1"
            onChange={(feature) => handleLocalChange(feature, 0)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[1]}
            placeholder="Top features 2"
            onChange={(feature) => handleLocalChange(feature, 1)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[2]}
            placeholder="Top features 3"
            onChange={(feature) => handleLocalChange(feature, 2)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
        </div>
        <div className="flex flex-col gap-2">
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[3]}
            placeholder="Top features 4"
            onChange={(feature) => handleLocalChange(feature, 3)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[4]}
            placeholder="Top features 5"
            onChange={(feature) => handleLocalChange(feature, 4)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
          <InputWithIcon
            icon={<Heater className="h-4 w-4" />}
            value={localFeatures[5]}
            placeholder="Top features 6"
            onChange={(feature) => handleLocalChange(feature, 5)}
            onBlur={handleBlur}
            className={cn('pl-0', className)}
            inputClassName="w-48"
          />
        </div>
      </div>
    </div>
  );
}
